﻿﻿﻿'use client';

import { useState, useRef, useEffect, useLayoutEffect, isValidElement, useCallback } from 'react';
import { Pencil, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const BACKGROUND_INTERACTIVE_SELECTOR = [
	'button',
	'a',
	'input',
	'textarea',
	'select',
	'label',
	'[role="button"]',
	'[data-background-interactive="true"]',
	'[data-prevent-background-edit="true"]',
].join(', ');

const normalizeHexColor = (value, fallback = '#FFFFFF') => {
	if (typeof value !== 'string') return fallback;
	const trimmed = value.trim();
	if (!/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trimmed)) {
		return fallback;
	}
	if (trimmed.length === 4) {
		const r = trimmed[1];
		const g = trimmed[2];
		const b = trimmed[3];
		return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
	}
	return trimmed.toUpperCase();
};

const formatFieldName = (fieldName) => {
	if (!fieldName) return '';

	const sanitized = fieldName
		.replace(/\[\d+\]/g, ' ')
		.replace(/\.|_/g, ' ')
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/\s+/g, ' ')
		.trim();

	return sanitized
		.split(' ')
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
		.join(' ');
};

const setMergedRef = (ref, node) => {
	if (typeof ref === 'function') {
		ref(node);
	} else if (ref && typeof ref === 'object') {
		ref.current = node;
	}
};

export default function EditableElement({
	children,
	type = 'text',
	value,
	onChange,
	isEditable = false,
	fieldName,
	displayName,
	onEditStart,
	className = '',
	multiline = false,
	placeholder = 'Click to edit...',
	useContentEditable = true,
	onFileSelected,
	isActive = false,
	isMobile = false,
	onPreviewEditStart,
	schemaFieldPath,
	showActiveBorder = false,
	overlayNote,
	overlayPlacement = 'bottom',
	...rest
}) {
	const { style: restStyle, ...restProps } = rest;
	const { showToast } = useAuth();
	const [isHovering, setIsHovering] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [isDragging, setIsDragging] = useState(false);
	const [editingValue, setEditingValue] = useState(type === 'color' ? normalizeHexColor(value, '#FFFFFF') : (value || ''));
	const [prevValue, setPrevValue] = useState(value);
	const editableRef = useRef(null);
	const fileInputRef = useRef(null);
	const colorInputRef = useRef(null);
	const backgroundWrapperRef = useRef(null);
	const clickTimeoutRef = useRef(null);
	const caretPositionRef = useRef(null);
	const [showBackgroundOverlay, setShowBackgroundOverlay] = useState(false);

	const getEventElementTarget = (target) => {
		if (target instanceof Element) {
			return target;
		}
		return target?.parentElement ?? null;
	};

	const isBackgroundInteractiveTarget = (elementTarget) => {
		if (!elementTarget) return false;
		const wrapperEl = backgroundWrapperRef.current;
		if (!wrapperEl) return false;

		const controlArea = elementTarget.closest?.('[data-background-control="true"]');
		if (controlArea) {
			return false;
		}

		const interactiveEl = elementTarget.closest?.(BACKGROUND_INTERACTIVE_SELECTOR);
		if (interactiveEl && wrapperEl.contains(interactiveEl) && interactiveEl !== wrapperEl) {
			return true;
		}

		const nestedEditable = elementTarget.closest?.('[data-editable-element="true"]');
		if (nestedEditable && nestedEditable !== wrapperEl) {
			return true;
		}

		return false;
	};

	const updateBackgroundHoverState = (event) => {
		if (type !== 'background') return;
		const wrapperEl = backgroundWrapperRef.current;
		if (!wrapperEl) return;

		const elementTarget = getEventElementTarget(event.target);
		if (!elementTarget) return;

		if (isBackgroundInteractiveTarget(elementTarget)) {
			setShowBackgroundOverlay(false);
			return;
		}

		setShowBackgroundOverlay(true);
	};

	const getCaretCharacterOffset = () => {
		if (typeof window === 'undefined' || !editableRef.current) return null;
		const selection = window.getSelection();
		if (!selection || selection.rangeCount === 0) return null;
		const range = selection.getRangeAt(0);
		if (!editableRef.current.contains(range.endContainer)) return null;
		const preRange = range.cloneRange();
		preRange.selectNodeContents(editableRef.current);
		preRange.setEnd(range.endContainer, range.endOffset);
		return preRange.toString().length;
	};

	const restoreCaretPosition = () => {
		if (typeof window === 'undefined' || caretPositionRef.current == null) return;
		const container = editableRef.current;
		if (!container || document.activeElement !== container) return;
		const selection = window.getSelection();
		if (!selection) return;
		const range = document.createRange();
		const targetOffset = Math.min(
			caretPositionRef.current,
			container.textContent?.length ?? 0
		);
		let remaining = targetOffset;
		let found = false;

		const traverse = (node) => {
			if (!node || found) return;
			if (node.nodeType === Node.TEXT_NODE) {
				const textLength = node.textContent?.length ?? 0;
				if (remaining <= textLength) {
					range.setStart(node, remaining);
					range.collapse(true);
					found = true;
					return;
				}
				remaining -= textLength;
				return;
			}
			for (let i = 0; i < node.childNodes.length; i += 1) {
				traverse(node.childNodes[i]);
				if (found) return;
			}
		};

		traverse(container);
		if (!found) {
			range.selectNodeContents(container);
			range.collapse(false);
		}
		selection.removeAllRanges();
		selection.addRange(range);
	};

	const attachNode = useCallback((node) => {
		editableRef.current = node;
		if (!isValidElement(children)) return;
		const originalRef = children.props?.ref ?? children.ref;
		setMergedRef(originalRef, node);
	}, [children]);

	const formattedFieldName = displayName || formatFieldName(fieldName);
	const dataAttributes = schemaFieldPath ? { "data-schema-field-path": schemaFieldPath } : {};
	const editableElementAttributes = {
		...dataAttributes,
		'data-editable-element': 'true',
		'data-editable-element-type': type,
	};
	const childClassName = isValidElement(children) && typeof children.props.className === 'string'
		? children.props.className
		: '';
	const displayClass = className && className.match(/\b(block|flex|inline-flex|inline-block|grid|hidden)\b/) ? '' : 'inline-block';
	const wrapperClassName = `relative group ${displayClass} ${className}`.trim();
	const wrapperStyle = restStyle || {};

	if (!isEditing && value !== prevValue) {
		setPrevValue(value);
		setEditingValue(type === 'color' ? normalizeHexColor(value, '#FFFFFF') : (value || ''));
	}

	if (type === 'background' && (!isHovering || isDragging) && showBackgroundOverlay) {
		setShowBackgroundOverlay(false);
	}

	useLayoutEffect(() => {
		if (!isEditing) {
			caretPositionRef.current = null;
			return;
		}
		restoreCaretPosition();
	}, [isEditing, editingValue]);

	useEffect(() => {
		if (isEditing && editableRef.current) {
			editableRef.current.focus();
			document.execCommand?.('selectAll', false);
		}
	}, [isEditing]);

	useEffect(() => {
		if (type === 'color' && isEditing && colorInputRef.current) {
			colorInputRef.current.focus();
			colorInputRef.current.showPicker?.();
		}
	}, [type, isEditing]);

	const finishEditing = (shouldSave = true, overrideValue) => {
		if (shouldSave && onChange) {
			const valueToPersist = overrideValue !== undefined ? overrideValue : editingValue ?? '';
			onChange(valueToPersist);
		}
		setIsEditing(false);
		onPreviewEditStart?.(null);
	};

	const handleClick = (e) => {
		e.stopPropagation();

		if (!isEditable) {
			return;
		}

		if (type === 'background') {
			const elementTarget = getEventElementTarget(e.target);
			if (isBackgroundInteractiveTarget(elementTarget)) {
				return;
			}
		}

		if (isMobile) {
			if (type === 'text' || type === 'color') {
				if (useContentEditable !== false) {
					setIsEditing(true);
					onPreviewEditStart?.(fieldName);
				}
				onEditStart?.(fieldName, type);
			} else if (type === 'image' || type === 'background' || type === 'video') {
				fileInputRef.current?.click();
				onEditStart?.(fieldName, type);
			}
			return;
		}

		if (type === 'text') {
			if (useContentEditable === false) {
				onEditStart?.(fieldName, type);
				return;
			}
			if (clickTimeoutRef.current) {
				clearTimeout(clickTimeoutRef.current);
				clickTimeoutRef.current = null;
				if (useContentEditable !== false) setIsEditing(true);
				onPreviewEditStart?.(fieldName);
			} else {
				clickTimeoutRef.current = setTimeout(() => {
					clickTimeoutRef.current = null;
					onEditStart?.(fieldName, type);
				}, 250);
			}
			return;
		}

		if (type === 'color') {
			if (clickTimeoutRef.current) {
				clearTimeout(clickTimeoutRef.current);
				clickTimeoutRef.current = null;
			}
			const normalizedColor = normalizeHexColor(value, '#FFFFFF');
			setEditingValue(normalizedColor);
			setIsEditing(true);
			onPreviewEditStart?.(fieldName);
			onEditStart?.(fieldName, type);
			return;
		}

		if (type === 'image' || type === 'background' || type === 'video') {
			fileInputRef.current?.click();
		}
	};

	const handleContentEditableChange = (e) => {
		caretPositionRef.current = getCaretCharacterOffset();
		const newValue = e.currentTarget.textContent ?? '';
		setEditingValue(newValue);
		onChange?.(newValue);
	};

	const handleInputChange = (e) => {
		const newValue = e.target.value;
		setEditingValue(newValue);
		onChange?.(newValue);
	};

	const handleKeyDown = (e) => {
		if (!multiline && e.key === 'Enter') {
			e.preventDefault();
			finishEditing();
		}
		if (e.key === 'Escape') {
			e.preventDefault();
			setEditingValue(value || '');
			finishEditing(false);
		}
	};

	const handleBlur = () => {
		finishEditing();
	};

	const handleFiles = (files) => {
		const file = files[0];
		if (!file) return;

		const isVideo = file.type.startsWith('video/');
		const isAudio = file.type.startsWith('audio/');
		const isImage = file.type.startsWith('image/');

		if (isImage && file.size > 5 * 1024 * 1024) {
			showToast("Ukuran gambar maksimal 5MB");
			return;
		}
		if (isVideo && file.size > 10 * 1024 * 1024) {
			showToast("Ukuran video maksimal 10MB");
			return;
		}
		if (isAudio && file.size > 5 * 1024 * 1024) {
			showToast("Ukuran audio maksimal 5MB");
			return;
		}

		if ((type === 'image' || type === 'background') && file.type.startsWith('image/')) {
			const blobUrl = URL.createObjectURL(file);
			onChange?.(blobUrl);
			onFileSelected?.(schemaFieldPath || fieldName, file, blobUrl);
			return;
		}

		if (type === 'video' && file.type.startsWith('video/')) {
			const blobUrl = URL.createObjectURL(file);
			onChange?.(blobUrl);
			onFileSelected?.(schemaFieldPath || fieldName, file, blobUrl);
		}
	};

	const handleFileChange = (e) => {
		const file = e.target.files?.[0];
		if (file) {
			handleFiles([file]);
		}
	};

	const handleDragEnter = (e) => {
		if (type === 'image' || type === 'background' || type === 'video') {
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(true);
			if (type === 'background') {
				setShowBackgroundOverlay(false);
			}
		}
	};

	const handleDragOver = (e) => {
		if (type === 'image' || type === 'background' || type === 'video') {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	const handleDragLeave = (e) => {
		if (type === 'image' || type === 'background' || type === 'video') {
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(false);
		}
	};

	const handleDrop = (e) => {
		if (type === 'image' || type === 'background' || type === 'video') {
			e.preventDefault();
			e.stopPropagation();
			setIsDragging(false);
			if (type === 'background') {
				setShowBackgroundOverlay(false);
			}
			const files = Array.from(e.dataTransfer.files);
			handleFiles(files);
		}
	};

	if (!isEditable) {
		return <>{children}</>;
	}

	if (type === 'text') {
		return (
			<div
				{...editableElementAttributes}
				className={wrapperClassName}
				onMouseEnter={() => setIsHovering(true)}
				onMouseLeave={() => setIsHovering(false)}
				onClick={handleClick}
				style={wrapperStyle}
				{...restProps}
			>
				{useContentEditable ? (
					isValidElement(children) ? (
						(() => {
							const ChildType = children.type;
							return (
								<ChildType
									{...children.props}
									key={children.key}
									ref={attachNode}
									contentEditable={isEditing}
									suppressContentEditableWarning={true}
									onInput={handleContentEditableChange}
									onKeyDown={handleKeyDown}
									onBlur={handleBlur}
									className={`${childClassName} outline-none transition-all cursor-text ${
										isHovering || isActive || showActiveBorder || isEditing
											? 'ring-2 ring-[#a47864] rounded px-1'
											: ''
									}`.trim()}
								>
									{isEditing ? editingValue : children.props.children}
								</ChildType>
							);
						})()
					) : (
						<div
							ref={editableRef}
							contentEditable={isEditing}
							suppressContentEditableWarning
							onInput={handleContentEditableChange}
							onKeyDown={handleKeyDown}
							onBlur={handleBlur}
							className={`outline-none transition-all cursor-text ${
								isHovering || isActive || showActiveBorder || isEditing
										? 'ring-2 ring-[#a47864] rounded px-1'
									: ''
							}`.trim()}
						>
							{isEditing ? editingValue : children}
						</div>
					)
				) : (
					<>
						{isEditing ? (
							multiline ? (
								<textarea
									value={editingValue}
									onChange={handleInputChange}
									onKeyDown={handleKeyDown}
									onBlur={handleBlur}
									autoFocus
									placeholder={placeholder}
									className={`w-full bg-white/90 backdrop-blur-sm border-2 rounded-lg px-2 py-1 outline-none resize-none ${childClassName}`}
									style={{ borderColor: '#a47864' }}
									rows={3}
								/>
							) : (
								<input
									type='text'
									autoComplete='off'
									value={editingValue}
									onChange={handleInputChange}
									onKeyDown={handleKeyDown}
									onBlur={handleBlur}
									autoFocus
									placeholder={placeholder}
									className={`w-full bg-white/90 backdrop-blur-sm border-2 rounded-lg px-2 py-1 outline-none ${childClassName}`}
									style={{ borderColor: '#a47864' }}
								/>
							)
						) : (
							<div
								className={`cursor-text transition-all${
									isHovering || showActiveBorder ? ' ring-2 ring-[#a47864] rounded px-1' : ''
								}`}
							>
								{children}
							</div>
						)}
					</>
				)}

				{isHovering && !isEditing && (
					<div
						className='absolute -top-6 left-0 text-white text-xs px-2 py-1 rounded flex items-center gap-1 whitespace-nowrap z-50 pointer-events-none'
						style={{ backgroundColor: '#a47864' }}
					>
						<Pencil size={10} />
						<span>Click to edit {formattedFieldName}</span>
					</div>
				)}

			</div>
		);
	}

	if (type === 'image' || type === 'background' || type === 'video') {
		const isBackgroundType = type === 'background';
		const isVideoType = type === 'video';
		const shouldShowOverlay = !isDragging && (isBackgroundType ? showBackgroundOverlay : isHovering);
		const changeLabel = isBackgroundType ? 'Change Background' : isVideoType ? 'Change Video' : 'Change Image';
		const dropLabel = isBackgroundType ? 'Background' : isVideoType ? 'Video' : 'Image';
		const OverlayIcon = isVideoType ? VideoIcon : ImageIcon;
		const overlayPositionClasses = overlayPlacement === 'top' ? 'top-4 left-0 right-0' : 'bottom-4 left-0 right-0';

		return (
			<div
				{...editableElementAttributes}
				className={`relative group ${className} ${isDragging ? 'ring-4 ring-primary-700' : ''}`}
				onMouseEnter={(event) => {
					setIsHovering(true);
					if (isBackgroundType) {
						updateBackgroundHoverState(event);
					}
				}}
				onMouseLeave={() => {
					setIsHovering(false);
					if (isBackgroundType) {
						setShowBackgroundOverlay(false);
					}
				}}
				onDragEnter={handleDragEnter}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				onClick={handleClick}
				ref={isBackgroundType ? backgroundWrapperRef : null}
				onMouseMove={isBackgroundType ? updateBackgroundHoverState : undefined}
			>
				{children}

				{onFileSelected && (
					<input
						ref={fileInputRef}
						type='file'
					accept={isVideoType ? 'video/mp4,video/webm,video/quicktime' : 'image/jpeg,image/png,image/webp,image/gif'}
						className='hidden'
						onChange={handleFileChange}
					/>
				)}

				{isDragging && (
					<div
						className='absolute inset-0 bg-black/60 flex items-center justify-center pointer-events-none'
						style={{ zIndex: 40 }}
					>
						<div
							className='text-white px-6 py-3 rounded-lg flex flex-col items-center gap-2'
							style={{ backgroundColor: '#a47864' }}
						>
							<OverlayIcon size={24} />
							<span className='font-semibold'>
								Drop {dropLabel} Here
							</span>
						</div>
					</div>
				)}

				{shouldShowOverlay && (
					<>
						<div
							className='absolute inset-0 bg-black/40 pointer-events-none'
							style={{ zIndex: 25 }}
						/>
						<div
							className={`absolute flex flex-col items-center gap-2 px-4 text-center ${overlayPositionClasses}`}
							style={{ zIndex: 30 }}
							data-background-control={isBackgroundType ? 'true' : undefined}
						>
							{overlayNote && (
								<span className='pointer-events-none text-xs text-white/90 leading-snug whitespace-pre-line'>
									{overlayNote}
								</span>
							)}
							<button
								type='button'
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleClick(e);
								}}
								className='pointer-events-auto flex items-center gap-2 bg-[#a47864] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow'
							>
								<OverlayIcon size={14} />
								<span>{changeLabel}</span>
							</button>
						</div>
					</>
				)}
			</div>
		);
	}

	if (type === 'color') {
		const currentColorValue = normalizeHexColor(isEditing ? editingValue : value, '#FFFFFF');

		const handleColorChange = (event) => {
			const newValue = normalizeHexColor(event.target.value, currentColorValue);
			setEditingValue(newValue);
			finishEditing(true, newValue);
		};

		const handleColorBlur = () => {
			finishEditing(false);
		};

		return (
			<div
				{...editableElementAttributes}
				className={`relative group ${className}`}
				onMouseEnter={() => setIsHovering(true)}
				onMouseLeave={() => setIsHovering(false)}
				onClick={handleClick}
			>
				{children}

				<input
					ref={colorInputRef}
					type="color"
					value={currentColorValue}
					onChange={handleColorChange}
					onBlur={handleColorBlur}
					className="sr-only"
					aria-hidden="true"
				/>

				{isHovering && !isEditing && (
					<div
						className='absolute -top-8 left-0 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-50 pointer-events-none'
						style={{ backgroundColor: '#a47864' }}
					>
						<span>Click to change color</span>
					</div>
				)}
			</div>
		);
	}

	return <>{children}</>;
}
    
